import React from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Divider, Stack, Typography } from '@mui/material';
import { get, post } from '../../utils/api';
import PortalPageHeader from '../../components/portal/PortalPageHeader';

function formatMoney(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

function formatDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return '—';
  }
}

export default function PortalPledgePaymentReceiptPage() {
  const { id: devoteeId, pledgeOrderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const fromNav = location.state;
    if (fromNav && (fromNav.wooOrderId != null || fromNav.idempotent)) {
      setResult(fromNav);
      return;
    }
    const pi = searchParams.get('payment_intent');
    if (!pi) {
      if (!fromNav) setError('Missing payment confirmation. Return to Pledges and try again, or use the link from your email if the payment completed.');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const fin = await post(
          `/portal/pledges/${encodeURIComponent(String(pledgeOrderId))}/finalize-payment`,
          { paymentIntentId: pi, customerComment: '' },
          { timeout: 120000 }
        );
        if (cancelled) return;
        const paySummary = await get(`/portal/pledges/${encodeURIComponent(String(pledgeOrderId))}/pay-summary`, {});
        const first = Array.isArray(paySummary?.pledge?.line_items) ? paySummary.pledge.line_items[0] : null;
        const lineName = String(first?.name || 'Pledge').trim() || 'Pledge';
        const names = lineName.includes(' - ')
          ? lineName.split(' - ').map((p) => p.trim()).filter(Boolean)
          : [lineName];
        setResult({
          ...(fin || {}),
          pledge: {
            ...(fin?.pledge || {}),
            id: Number(fin?.pledge?.id || pledgeOrderId),
            productName: fin?.pledge?.productName || names[0] || 'Pledge',
            variationName: fin?.pledge?.variationName || (names.length > 1 ? names.slice(1).join(' - ') : null),
          },
          pledgeDate: fin?.pledgeDate || paySummary?.pledge?.date_created_gmt || paySummary?.pledge?.date_created || null,
        });
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Could not confirm payment');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.state, searchParams, pledgeOrderId]);

  return (
    <>
      <PortalPageHeader title="Payment receipt" />
      {loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Confirming your payment…
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {result && !error && (
        <Box sx={{ maxWidth: 560, mx: 'auto' }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            Thank you for contributing to your pledge. Here are your contribution details.
            {result.billingEmail ? ` An email receipt was sent to ${result.billingEmail}.` : ''}
          </Alert>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Contribution reference #</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.25 }}>
                #{result.wooOrderId}
              </Typography>
              <Divider sx={{ my: 1.25 }} />

              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>Amount:</strong> {formatMoney(result?.amounts?.total)}
                </Typography>
                <Typography variant="body2">
                  <strong>Date of contribution:</strong> {formatDate(result?.contributionDate)}
                </Typography>
                <Typography variant="body2">
                  <strong>Contribution for:</strong> {result?.pledge?.productName || 'Pledge'}
                  {result?.pledge?.variationName ? ` — ${result.pledge.variationName}` : ''}
                </Typography>
                <Typography variant="body2">
                  <strong>Pledge order #:</strong> {result?.pledge?.id || pledgeOrderId}
                </Typography>
                <Typography variant="body2">
                  <strong>Payment method:</strong> {result?.paymentMethodTitle || 'Card / ACH'}
                </Typography>
                {result.paymentIntentId ? (
                  <Typography variant="body2" color="text.secondary">
                    Stripe ref: <strong>{String(result.paymentIntentId)}</strong>
                  </Typography>
                ) : null}
                {result.idempotent ? (
                  <Typography variant="body2" color="text.secondary">
                    (This payment was already recorded.)
                  </Typography>
                ) : null}
              </Stack>
            </CardContent>
          </Card>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ mt: 3 }}
            justifyContent="center"
            alignItems="center"
          >
            <Button variant="contained" onClick={() => navigate(`/devotees/${devoteeId}/pledges`)}>
              Back to Pledges
            </Button>
            <Button
              variant="outlined"
              onClick={() =>
                navigate(`/devotees/${devoteeId}/pledges`, { state: { openPledgeId: Number(pledgeOrderId) } })
              }
            >
              Show Pledge Details
            </Button>
            <Button variant="outlined" onClick={() => navigate(`/devotees/${devoteeId}/donations`)}>
              My Donations
            </Button>
          </Stack>
        </Box>
      )}
    </>
  );
}
